'use client'

import 'react-easy-crop/react-easy-crop.css'

import { useCallback, useEffect, useState } from 'react'
import Cropper, { type Area } from 'react-easy-crop'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { croppedBlobToFile, getCroppedSquareBlob } from '@/lib/canvas-crop'
import { cn } from '@/lib/utils'

type ImageSquareCropDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Object URL from the selected file (caller must revoke when done). */
  imageSrc: string | null
  onCancel: () => void
  /** Called with a square JPEG file; caller uploads it. */
  onCropComplete: (file: File) => void
  title?: string
  description?: string
  /** Output filename for the cropped file (e.g. `avatar.jpg`). */
  outputFileName?: string
}

export function ImageSquareCropDialog({
  open,
  onOpenChange,
  imageSrc,
  onCancel,
  onCropComplete,
  title = 'Crop image',
  description = 'Drag to reposition. Use the zoom slider to fit your subject inside the square.',
  outputFileName = 'avatar.jpg',
}: ImageSquareCropDialogProps) {
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
  const [applying, setApplying] = useState(false)

  useEffect(() => {
    if (open) {
      setCrop({ x: 0, y: 0 })
      setZoom(1)
      setCroppedAreaPixels(null)
    }
  }, [open, imageSrc])

  const onCropCompleteCb = useCallback((_area: Area, areaPx: Area) => {
    setCroppedAreaPixels(areaPx)
  }, [])

  const handleOpenChange = (next: boolean) => {
    if (!next) onCancel()
    onOpenChange(next)
  }

  const handleApply = async () => {
    if (!imageSrc || !croppedAreaPixels) return
    setApplying(true)
    try {
      const blob = await getCroppedSquareBlob(imageSrc, croppedAreaPixels)
      const file = croppedBlobToFile(blob, outputFileName.replace(/\.[^.]+$/, '.jpg'))
      onCropComplete(file)
      handleOpenChange(false)
    } finally {
      setApplying(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {/* Stack above other modals (e.g. Create profile / Create team) which also use z-50 */}
      <DialogContent
        className="z-[101] max-w-lg gap-0 overflow-hidden p-0 sm:max-w-lg"
        overlayClassName="z-[100]"
      >
        <DialogHeader className="border-b px-6 py-4">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="relative h-[min(55vh,360px)] w-full bg-muted">
          {imageSrc ? (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="rect"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropCompleteCb}
            />
          ) : null}
        </div>

        <div className="space-y-2 border-t bg-background px-6 py-4">
          <Label htmlFor="crop-zoom" className="text-xs text-muted-foreground">
            Zoom
          </Label>
          <Input
            id="crop-zoom"
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className={cn('w-full cursor-pointer accent-primary')}
          />
        </div>

        <DialogFooter className="border-t px-6 py-4 sm:justify-end">
          <Button type="button" variant="ghost" onClick={() => handleOpenChange(false)} disabled={applying}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void handleApply()} disabled={!imageSrc || !croppedAreaPixels || applying}>
            {applying ? 'Saving…' : 'Apply'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
